import { apiService } from './api';

// TypeScript Interfaces
export interface CartItem {
  id: string;
  orderId: string;
  siteId: string;
  siteUrl: string;
  siteDa: number;
  siteDr: number;
  siteTraffic: number;
  price: number;
  publisherName: string;
  publisherEmail: string | null;
  contentText?: string | null;
  contentDocUrl?: string | null;
  contentUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CartResponse {
  success: boolean;
  data: {
    projectId: string;
    projectName: string;
    clientId: string;
    clientName: string;
    clientBudget: number;
    projectBudget: number;
    cartItems: CartItem[];
    cartTotal: number;
    cartCount: number;
    budgetRemaining: number;
    budgetUsedPercent: number;
  };
}

export interface AddToCartRequest {
  projectId: string;
  clientId: string;
  siteId: string;
  contentText?: string;
  contentDocUrl?: string;
  contentUrl?: string;
  orderDate?: string;
  submissionType?: 'GBS' | 'LI' | 'BOTH';
  price?: number;
}

export interface AddToCartResponse {
  success: boolean;
  message?: string;
  data: {
    orderId: string;
    orderNumber: string;
    siteUrl: string;
    price: number;
    cartCount: number;
    cartTotal: number;
  };
}

export interface RemoveFromCartResponse {
  success: boolean;
  message: string;
  data: {
    cartCount: number;
    cartTotal: number;
  };
}

export interface CheckoutRequest {
  projectId: string;
  orderMonth: number;
  orderYear: number;
  notes?: string;
  planId?: string;
}

export interface PlacementSummary {
  id: string;
  placementId: string;
  orderId: string;
  siteUrl: string;
  price: number;
  status: string;
  orderDate: string;
}

export interface CheckoutResponse {
  success: boolean;
  message: string;
  data: {
    placementsCreated: number;
    placements: PlacementSummary[];
    totalCost: number;
  };
}

export interface CreateStripeCheckoutSessionRequest {
  projectId: string;
  orderMonth: number;
  orderYear: number;
  notes?: string;
  planId?: string;
  successUrl: string;
  cancelUrl: string;
}

export interface CreateStripeCheckoutSessionResponse {
  success: boolean;
  data: {
    paymentAttemptId: string;
    sessionId: string;
    checkoutUrl: string;
  };
}

export interface FinalizeStripeCheckoutRequest {
  paymentAttemptId: string;
  sessionId: string;
}

export interface FinalizeStripeCheckoutResponse {
  success: boolean;
  message: string;
  data: {
    placementsCreated: number;
    placements: PlacementSummary[];
    totalCost: number;
    alreadyFinalized?: boolean;
  };
}

export interface CreateOrdersStripeCheckoutSessionRequest {
  orderIds: string[];
  orderMonth: number;
  orderYear: number;
  notes?: string;
  successUrl: string;
  cancelUrl: string;
}

export interface FinalizeOrdersStripeCheckoutRequest {
  paymentAttemptId: string;
  sessionId: string;
}

export interface AllCartItemsResponse {
  success: boolean;
  data: {
    projects: Array<{
      projectId: string;
      projectName: string;
      projectBudget: number;
      clientId: string;
      clientName: string;
      clientBudget: number;
      cartItems: CartItem[];
      cartTotal: number;
      cartCount: number;
    }>;
    totalItems: number;
    totalAmount: number;
  };
}

export interface ErrorResponse {
  success: false;
  error: string;
}

class CartService {
  /**
   * Get all cart items across all projects for current user
   */
  async getAllCartItems(): Promise<AllCartItemsResponse> {
    try {
      const response = await apiService.get<AllCartItemsResponse>('/cart/all/items');
      return response;
    } catch (error: any) {
      console.error('Error fetching all cart items:', error);
      throw error;
    }
  }

  /**
   * Get all cart items for a specific project
   */
  async getCartItems(projectId: string): Promise<CartResponse> {
    try {
      // Cache-bust so the browser never serves a stale (HTTP 304) cart body.
      // The cart changes frequently (add/remove/checkout) and a stale cache
      // keeps the UI out of sync with the DB (e.g. "Add" button never flips to
      // "Added"). A unique query param forces a fresh 200 response every time.
      const response = await apiService.get<CartResponse>(`/cart/${projectId}`, {
        _t: Date.now(),
      });
      return response;
    } catch (error: any) {
      console.error('Error fetching cart items:', error);
      throw error;
    }
  }

  /**
   * Add a site to the cart for a specific project
   */
  async addToCart(data: AddToCartRequest): Promise<AddToCartResponse> {
    try {
      const response = await apiService.post<AddToCartResponse>('/cart', data);
      return response;
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Remove an item from the cart
   */
  async removeFromCart(orderId: string): Promise<RemoveFromCartResponse> {
    try {
      const response = await apiService.delete<RemoveFromCartResponse>(`/cart/${orderId}`);
      return response;
    } catch (error: any) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  async updateCartItem(
    cartItemId: string,
    data: {
      contentDocUrl?: string | null;
      contentText?: string | null;
      contentUrl?: string | null;
      orderDate?: string;
      submissionType?: 'GBS' | 'LI' | 'BOTH';
      price?: number;
    }
  ): Promise<{ success: boolean; message?: string }> {
    try {
      return await apiService.patch<{ success: boolean; message?: string }>(`/cart/items/${cartItemId}`, data);
    } catch (error: any) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  }

  /**
   * Checkout cart - create placements from cart items
   */
  async checkout(data: CheckoutRequest): Promise<CheckoutResponse> {
    try {
      const response = await apiService.post<CheckoutResponse>('/cart/checkout', data);
      return response;
    } catch (error: any) {
      console.error('Error during checkout:', error);
      throw error;
    }
  }

  async createStripeCheckoutSession(
    data: CreateStripeCheckoutSessionRequest
  ): Promise<CreateStripeCheckoutSessionResponse> {
    try {
      const response = await apiService.post<CreateStripeCheckoutSessionResponse>(
        '/payments/stripe/create-checkout-session',
        data
      );
      return response;
    } catch (error: any) {
      console.error('Error creating Stripe checkout session:', error);
      throw error;
    }
  }

  async finalizeStripeCheckout(
    data: FinalizeStripeCheckoutRequest
  ): Promise<FinalizeStripeCheckoutResponse> {
    try {
      const response = await apiService.post<FinalizeStripeCheckoutResponse>(
        '/payments/finalize',
        data
      );
      return response;
    } catch (error: any) {
      console.error('Error finalizing Stripe checkout:', error);
      throw error;
    }
  }

  async createOrdersStripeCheckoutSession(
    data: CreateOrdersStripeCheckoutSessionRequest
  ): Promise<CreateStripeCheckoutSessionResponse> {
    try {
      const response = await apiService.post<CreateStripeCheckoutSessionResponse>(
        '/payments/stripe/create-orders-checkout-session',
        data
      );
      return response;
    } catch (error: any) {
      console.error('Error creating Stripe checkout session for orders:', error);
      throw error;
    }
  }

  async finalizeOrdersStripeCheckout(
    data: FinalizeOrdersStripeCheckoutRequest
  ): Promise<FinalizeStripeCheckoutResponse> {
    try {
      const response = await apiService.post<FinalizeStripeCheckoutResponse>(
        '/payments/finalize-orders',
        data
      );
      return response;
    } catch (error: any) {
      console.error('Error finalizing Stripe checkout for orders:', error);
      throw error;
    }
  }

  /**
   * Get cart summary for multiple projects (used in projects list)
   */
  async getCartSummaries(projectIds: string[]): Promise<Record<string, { count: number; total: number }>> {
    // This will be populated from the projects API response
    // which already includes cartCount and cartTotal
    return {};
  }

  /**
   * Clear entire cart for a project (remove all items)
   */
  async clearCart(projectId: string): Promise<void> {
    try {
      // Get all cart items
      const cartResponse = await this.getCartItems(projectId);
      
      // Remove each item
      const removePromises = cartResponse.data.cartItems.map(item => 
        this.removeFromCart(item.id)
      );
      
      await Promise.all(removePromises);
    } catch (error: any) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }

  /**
   * Check if a site is already in the cart for a project
   */
  async isSiteInCart(projectId: string, siteId: string): Promise<boolean> {
    try {
      const cartResponse = await this.getCartItems(projectId);
      return cartResponse.data.cartItems.some(item => item.siteId === siteId);
    } catch (error: any) {
      console.error('Error checking if site is in cart:', error);
      return false;
    }
  }

  /**
   * Get cart count for a specific project
   */
  async getCartCount(projectId: string): Promise<number> {
    try {
      const cartResponse = await this.getCartItems(projectId);
      return cartResponse.data.cartCount;
    } catch (error: any) {
      console.error('Error getting cart count:', error);
      return 0;
    }
  }

  /**
   * Get cart total for a specific project
   */
  async getCartTotal(projectId: string): Promise<number> {
    try {
      const cartResponse = await this.getCartItems(projectId);
      return cartResponse.data.cartTotal;
    } catch (error: any) {
      console.error('Error getting cart total:', error);
      return 0;
    }
  }

  /**
   * Validate budget before adding to cart
   */
  async validateBudget(projectId: string, additionalAmount: number): Promise<{
    valid: boolean;
    warning?: string;
    projectBudget: number;
    currentTotal: number;
    newTotal: number;
    budgetRemaining: number;
  }> {
    try {
      const cartResponse = await this.getCartItems(projectId);
      const currentTotal = cartResponse.data.cartTotal;
      const newTotal = currentTotal + additionalAmount;
      const projectBudget = cartResponse.data.projectBudget;
      const budgetRemaining = projectBudget - newTotal;

      if (newTotal > projectBudget) {
        return {
          valid: false,
          warning: `Adding this item will exceed the project budget by $${(newTotal - projectBudget).toFixed(2)}`,
          projectBudget,
          currentTotal,
          newTotal,
          budgetRemaining
        };
      }

      return {
        valid: true,
        projectBudget,
        currentTotal,
        newTotal,
        budgetRemaining
      };
    } catch (error: any) {
      console.error('Error validating budget:', error);
      throw error;
    }
  }
}

export const cartService = new CartService();
export default cartService;
